import React, { useState } from 'react';
import './PricingSection.css';

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  recommended?: boolean;
  cta: string;
}

const plans: PricingPlan[] = [
  {
    name: 'Free',
    price: '0',
    period: 'forever',
    description: 'Perfect for getting started with basic health tracking.',
    features: [
      // Phase 2: 'Book up to 2 lab tests/month' — lab scheduling hidden for Phase 1
      // 'Book up to 2 lab tests/month',
      'Scan up to 5 reports/month',
      'Digital health reports',
      'Basic health score',
      'Email notifications',
      'Standard support',
    ],
    cta: 'Get Started Free',
  },
  {
    name: 'Pro',
    price: '299',
    period: '/month',
    description: 'For individuals serious about their health journey.',
    features: [
      // Phase 2: 'Unlimited lab test bookings' — lab scheduling hidden for Phase 1
      // 'Unlimited lab test bookings',
      'Unlimited report scans & AI analysis',
      'Advanced health score & trends',
      'Personalized diet plans',
      'Push & SMS notifications',
      'Priority support',
      'Health report sharing',
    ],
    recommended: true,
    cta: 'Start Pro Trial',
  },
  {
    name: 'Family',
    price: '499',
    period: '/month',
    description: 'Manage health for your entire family in one place.',
    features: [
      'Everything in Pro',
      'Up to 5 family members',
      'Family health dashboard',
      'Shared health reports',
      'Dedicated health advisor',
      // Phase 2: Annual health checkup packs — lab packages hidden for Phase 1
      // 'Annual health checkup packs',
      'Family health score analytics',
    ],
    cta: 'Start Family Trial',
  },
];

const PricingSection: React.FC = () => {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="pricing">
      <div className="container">
        <h2 className="section-title">Simple, Transparent Pricing</h2>
        <p className="section-subtitle">
          Choose the plan that fits your health needs. All plans include a 14-day free trial.
        </p>

        <div className="pricing__toggle">
          <span className={!annual ? 'pricing__toggle-active' : ''}>Monthly</span>
          <button
            className={`pricing__toggle-switch ${annual ? 'pricing__toggle-switch--on' : ''}`}
            onClick={() => setAnnual(!annual)}
            aria-label="Toggle annual pricing"
          >
            <span className="pricing__toggle-knob" />
          </button>
          <span className={annual ? 'pricing__toggle-active' : ''}>
            Annual <span className="pricing__toggle-save">Save 20%</span>
          </span>
        </div>

        <div className="pricing__grid">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`pricing__card ${plan.recommended ? 'pricing__card--recommended' : ''}`}
            >
              {plan.recommended && (
                <div className="pricing__badge">Most Popular</div>
              )}
              <h3 className="pricing__plan-name">{plan.name}</h3>
              <div className="pricing__price">
                <span className="pricing__currency">&#8377;</span>
                <span className="pricing__amount">
                  {plan.price === '0'
                    ? '0'
                    : annual
                    ? Math.round(parseInt(plan.price) * 0.8)
                    : plan.price}
                </span>
                <span className="pricing__period">{plan.period}</span>
              </div>
              <p className="pricing__description">{plan.description}</p>
              <ul className="pricing__features">
                {plan.features.map((feature, i) => (
                  <li key={i} className="pricing__feature">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href="#download"
                className={`btn ${plan.recommended ? 'btn-primary' : 'btn-outline'} pricing__cta`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
